import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Button, Chip, CircularProgress, Alert, Menu, MenuItem, ListItemIcon
} from '@mui/material';
import { Add, Refresh, MoreVert, Edit, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllTimeSlots, deleteTimeSlot } from '../../services/Api';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllTimeSlots = () => {
    const navigate = useNavigate();
    const [timeSlots, setTimeSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Action Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);

    const fetchTimeSlots = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllTimeSlots();
            if (response.success || Array.isArray(response.data) || Array.isArray(response)) {
                const data = response.data || response;
                let slotsArray = Array.isArray(data) ? [...data] : [];
                
                const parseTime = (timeStr) => {
                    if (!timeStr) return 0;
                    const match = timeStr.toString().match(/(\d+):(\d+)\s*(AM|PM)?/i);
                    if (!match) return 0;
                    let [, hours, minutes, modifier] = match;
                    hours = parseInt(hours, 10);
                    minutes = parseInt(minutes, 10);
                    if (modifier) {
                        if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
                        if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
                    }
                    return hours * 60 + minutes;
                };

                const parseDate = (dStr) => {
                    if (!dStr) return 0;
                    let d = new Date(dStr);
                    if (!isNaN(d.getTime())) return d.getTime();
                    const parts = dStr.split('/');
                    if (parts.length === 3) {
                        d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                        if (!isNaN(d.getTime())) return d.getTime();
                    }
                    return 0;
                };

                slotsArray.sort((a, b) => {
                    const dateA = parseDate(a.date);
                    const dateB = parseDate(b.date);
                    
                    if (dateA !== dateB) {
                        return dateB - dateA; // Descending by date
                    }
                    
                    const timeA = parseTime(a.startSlot || (a.slot ? a.slot.split('-')[0] : ''));
                    const timeB = parseTime(b.startSlot || (b.slot ? b.slot.split('-')[0] : ''));
                    
                    return timeA - timeB; // Ascending by time within the same date
                });

                setTimeSlots(slotsArray);
            } else {
                setError(response.message || 'Failed to fetch time slots');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching time slots');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimeSlots();
    }, []);

    const handleMenuOpen = (event, slot) => {
        setAnchorEl(event.currentTarget);
        setSelectedSlot(slot);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedSlot(null);
    };

    const handleEdit = () => {
        navigate(`/timeslots/modify/${selectedSlot.id}`, { state: { slotData: selectedSlot } });
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setDeleteId(selectedSlot.id);
        setIsDeleteDialogOpen(true);
        handleMenuClose();
    };

    const confirmDelete = async () => {
        setDeleteError(null);
        try {
            const response = await deleteTimeSlot(deleteId);
            if (response.success) {
                setTimeSlots(timeSlots.filter(s => s.id !== deleteId));
                setIsDeleteDialogOpen(false);
            } else {
                setDeleteError(response.message || 'Failed to delete time slot');
            }
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Error deleting time slot');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        Time Slots
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage available delivery/service time intervals
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton 
                        onClick={fetchTimeSlots} 
                        disabled={loading} 
                        sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}
                    >
                        <Refresh />
                    </IconButton>
                    <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        onClick={() => navigate('/timeslots/modify')}
                        sx={{ px: 4, backgroundColor: 'primary.main' }}
                    >
                        Add Time Slot
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {deleteError && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>{deleteError}</Alert>}

            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Slot Interval</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Module Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : timeSlots.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No time slots found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                timeSlots.map((slot, index) => (
                                    <TableRow key={slot.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{slot.date || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {slot.startSlot && slot.endSlot
                                                ? `${slot.startSlot} - ${slot.endSlot}`
                                                : slot.slot || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {slot.moduleType ? (
                                                <Chip
                                                    label={slot.moduleType}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'primary.light',
                                                        color: 'primary.contrastText',
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={slot.isActive ? "Active" : "Inactive"} 
                                                size="small"
                                                color={slot.isActive ? "success" : "default"}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, slot)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon><Edit fontSize="small" color="primary" /></ListItemIcon>
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm 
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Time Slot"
                message="Are you sure you want to delete this time slot? This action cannot be undone."
            />
        </Box>
    );
};

export default GetAllTimeSlots;
